import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import type { IDADocument } from "../common/types";
import { processHtmlWithAssets } from "../common/htmlPreview";
import DocumentPreviewModal from "./DocumentPreviewModal";

const mockAuthFetch = jest.fn();
const mockResolveAssets = jest.fn();
const mockGetDocumentAccess = jest.fn();

jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));

jest.mock("../services/api", () => ({
  useDownloadDocument: () => jest.fn(),
  useResolveAssets: () => mockResolveAssets,
  useGetDocumentAccess: () => mockGetDocumentAccess,
  useUpdateDocumentPublish: () => jest.fn(),
  useUpdateDocumentPublic: () => jest.fn(),
  useUpdateDocumentPassword: () => jest.fn(),
}));

jest.mock("../hooks/useMMDataBridge", () => ({
  useMMDataBridge: () => ({ current: null }),
}));

jest.mock("../common/htmlPreview", () => ({
  ...jest.requireActual("../common/htmlPreview"),
  processHtmlWithAssets: jest.fn((html: string) => html),
}));

jest.mock("react-markdown", () => ({
  __esModule: true,
  default: ({ children }: { children?: unknown }) => <>{children}</>,
}));

jest.mock("remark-gfm", () => ({
  __esModule: true,
  default: () => {},
}));

const processHtmlMock = processHtmlWithAssets as jest.MockedFunction<
  typeof processHtmlWithAssets
>;

const document: IDADocument = {
  id: "workspace/doc-1",
  workspace_id: "workspace",
  filename: "dashboard.html",
  token_count: 0,
  file_size: 100,
  source_type: "generated",
  uploaded_at: "2026-07-17T00:00:00Z",
  index_status: "completed",
  backend: "local",
};

describe("DocumentPreviewModal mmData injection", () => {
  beforeEach(() => {
    mockAuthFetch.mockReset();
    mockResolveAssets.mockReset();
    mockGetDocumentAccess.mockReset();
    mockGetDocumentAccess.mockResolvedValue({});
    processHtmlMock.mockClear();
    processHtmlMock.mockImplementation((html) => html);
    Object.defineProperty(URL, "createObjectURL", {
      configurable: true,
      value: jest.fn(() => "blob:preview"),
    });
    Object.defineProperty(URL, "revokeObjectURL", {
      configurable: true,
      value: jest.fn(),
    });
  });

  const renderPreview = (html: string) => {
    mockAuthFetch.mockResolvedValueOnce({
      json: jest.fn().mockResolvedValue({ configured: false }),
    });
    mockAuthFetch.mockResolvedValueOnce({
      blob: jest.fn().mockResolvedValue({
        text: jest.fn().mockResolvedValue(html),
      }),
    });
    return render(
      <DocumentPreviewModal
        open
        document={document}
        workspaceId="workspace"
        onClose={jest.fn()}
      />,
    );
  };

  it("processes self-contained HTML exactly once with an empty asset map", async () => {
    const html = "<html><body>Dashboard</body></html>";

    renderPreview(html);

    await waitFor(() => expect(processHtmlMock).toHaveBeenCalledWith(html, {}));
    expect(processHtmlMock).toHaveBeenCalledTimes(1);
    expect(mockResolveAssets).not.toHaveBeenCalled();
  });

  it.each([
    { name: "empty", assetResult: {} },
    { name: "failed", assetResult: new Error("asset lookup failed") },
  ])(
    "processes linked HTML with an empty map when asset resolution is $name",
    async ({ assetResult }) => {
      const html = '<html><body><img src="chart.png"></body></html>';
      if (assetResult instanceof Error) {
        mockResolveAssets.mockRejectedValueOnce(assetResult);
      } else {
        mockResolveAssets.mockResolvedValueOnce(assetResult);
      }

      renderPreview(html);

      await waitFor(() =>
        expect(processHtmlMock).toHaveBeenCalledWith(html, {}),
      );
      expect(processHtmlMock).toHaveBeenCalledTimes(1);
    },
  );

  it("processes linked HTML exactly once with resolved assets", async () => {
    const html = '<html><body><img src="chart.png"></body></html>';
    const assets = {
      "chart.png": {
        data_uri: "data:image/png;base64,AA==",
        content_type: "image/png",
      },
    };
    mockResolveAssets.mockResolvedValueOnce(assets);

    renderPreview(html);

    await waitFor(() =>
      expect(processHtmlMock).toHaveBeenCalledWith(html, assets),
    );
    expect(processHtmlMock).toHaveBeenCalledTimes(1);
  });

  it("loads the returned content-host URL without fetching or inlining HTML", async () => {
    mockAuthFetch.mockResolvedValueOnce({
      json: jest.fn().mockResolvedValue({
        configured: true,
        url: "https://sharedby.example.test/c/session/reports/dashboard.html",
        origin: "https://sharedby.example.test",
      }),
    });

    render(
      <DocumentPreviewModal
        open
        document={document}
        workspaceId="workspace"
        onClose={jest.fn()}
      />,
    );

    await waitFor(() =>
      expect(screen.getByTitle("dashboard.html").getAttribute("src")).toBe(
        "https://sharedby.example.test/c/session/reports/dashboard.html",
      ),
    );
    expect(mockAuthFetch).toHaveBeenCalledTimes(1);
    expect(processHtmlMock).not.toHaveBeenCalled();
  });
});
